import * as Maths from "oc-commons/Maths";
import * as Commons from "oc-commons/Commons";
import { Bag, ForEach } from "oc-commons/Bag2";
import { MultiBag } from "oc-commons/MultiBag";
import { MultiMap } from "oc-commons/MultiMap";
import {
  Amount,
  binPackingFirstFitDecreasing,
  Size,
} from "oc-commons/binPackingFirstFitDecreasing";
import { MArtikel, MArtikelrelation, MKeyTable, MZuschnittsoptimiert } from "../../metamodell";
import { memoizeUniversal } from "oc-commons/memoizeMultiMap";
import { istUnecht } from "../RichMetadata";
import { Vector2 } from "three";

declare module "../../metamodell" {
  interface MArtikel {
    potentiellUnechteArtikel?: MArtikel[];
    unechterArtikel?: MArtikel;
    mass?: string;
  }
}

export type Money = number;
export type Index = number;
type ArtikelAmount = [MArtikel, Amount];

const decimals = 2;
const pow = Math.pow(10, decimals);
const toCent = (x: Money) => Maths.round(x * pow);
const unCent = (x: Money) => Maths.round(x / pow, 2);

export interface BestelllistenEintrag extends MArtikel {
  Anzahl: Size;
  Einzelpreis: Money;
  Preis: Money;
  Mass: string;
  ElementIndizes: Index[];
  JacoIds: number[];
  UserDefined?: boolean;
  StuecklistenanzahlEditierbar?: boolean;
  AnzahlEditiert?: boolean;
}

/**
 * "bauseits"|"{w} x {h} cm" oder "", z.B.:
 *  '166 x 180 cm'
 *  'bauseits'
 *  ''
 */
type TMassOrBauseitsTag = string;

/**
 * {Tor|Pfosten|UMProfil|Anker|...}_{id}
 * Die Einträge werden nach ObjectGroupTag gruppiert
 */
type TObjectGroupTag = string;

type TVariabel = "fix" | "fertigung" | "bauseits";

export interface BaugruppenEintrag extends MArtikel {
  Anzahl: Amount;
  MassOrBauseitsTag: TMassOrBauseitsTag;
  gruppenSortierung: number;
  ElementIndizes: Index[];
  JacoIds: number[];
}

export function vpeTimes(vpe: Amount, count: Amount) {
  return Math.ceil(count / vpe) * vpe;
}

class GroupInfo {
  ObjectGroupTag: TObjectGroupTag;
  MassOrBauseitsTag: TMassOrBauseitsTag | undefined = undefined;
  ElementIndex: Index | undefined = undefined;
  Variabel: TVariabel | undefined = undefined;
  // wird potentiell zugeschnitten
  objektBreiteBagVorZuschnitt = new MultiBag<[MKeyTable, Size | undefined]>();
  // Hier sind nach dem Zuschneiden alle Objekte drin
  // das Zuschneiden entfernt alle zugeschnittenen Objekte aus der objektBreiteBag der jeweiligen Gruppe
  // und fügt die zugeschnittene Anzahl in die objektBreiteBag der UNGROUPED hinzu
  objektBreiteBag = new MultiBag<[MKeyTable, Size | undefined]>();
  // damit die Bauliste auch die zugeschnittenen Objekte anzeigt in der originalen Gruppe, werden hier die zugeschnittenen
  // Objekte der Gruppe gezählt (natürlich in der ursprünglichen unzugeschnittenen Anzahl)
  objektBreiteBagGruppenzuschnitt = new MultiBag<[MKeyTable, Size | undefined]>();
  // die artikelBag eines Elementes (ObjektGroupTag) wird für die Baugruppen verwendet, die mit UNGROUPED für die Bestellliste
  artikelBag = new Bag<MArtikel>();
  // Artikel ohne Artikelnummer und ohne Preis, die in einer Klammer in einem Element (Gruppe) mit echten Artikeln stehen
  unechteArtikelBag = new Bag<MArtikel>();
  // Welche Artikel sind mit welchem Jaco verknüpft?
  artikelJacoIdsMap = new Map<MArtikel, Set<number>>();
  ensureJacoSetOfArtikel = ensureMap(this.artikelJacoIdsMap, (k) => new Set<number>());
  // Welche Objekte sind mit welchem Jaco verknüpft?
  objektJacoIdsMap = new Map<MKeyTable, Set<number>>();
  ensureJacoSetOfObjekt = ensureMap(this.objektJacoIdsMap, (k) => new Set<number>());
}

export interface StücklistenIteratorParams {
  ObjektMeta: MKeyTable;
  Artikel: MArtikel;
  /**
   * nur bei Artikel!=null gesetzt
   */
  Anzahl: Amount;
  ObjectGroupTag: TObjectGroupTag;
  MassOrBauseitsTag: TMassOrBauseitsTag;
  Variabel: TVariabel;
  /**
   * nur gesetzt für prinzipiell zuschnittsoptimierte Objekte (Füllungen)
   */
  Breite: Size;
  ElementIndex: Index;
  /**
   * Für Highlighting der Stückliste: Jaco, das gehighlighted wird, wenn MouseOver Stücklisteneintrag
   */
  JacoId: number;
}

export type StücklistenIterator = (params: Partial<StücklistenIteratorParams>) => void;

export const UNGROUPED = "";

function ensureMap<K, V>(map: Map<K, V>, create: (K) => V) {
  return (k: K) => {
    if (map.has(k)) {
      return map.get(k);
    }
    const v = create(k);
    map.set(k, v);
    return v;
  };
}

type Tartikel_unechterArtikel_mass = [MArtikel, MArtikel | undefined, string | undefined];

type TEnsureVirtuellenArtikel = (params: Tartikel_unechterArtikel_mass) => MArtikel;

export const createEnsureVirtuellenArtikel = (): TEnsureVirtuellenArtikel =>
  ensureMap<Tartikel_unechterArtikel_mass, MArtikel>(
    new MultiMap<Tartikel_unechterArtikel_mass, MArtikel>() as any as Map<
      Tartikel_unechterArtikel_mass,
      MArtikel
    >,
    ([Artikel, unechterArtikel, mass]: Tartikel_unechterArtikel_mass) => ({
      ...Artikel,
      unechterArtikel,
      mass,
    })
  );

type TArtikelSource = {
  collectArtikel: (iterator: StücklistenIterator) => void;
  userDefinedArticles?: { description: string; amount: number; price: number }[];
};

export function createStückliste(arrangement: TArtikelSource): {
  readonly bestellliste: BestelllistenEintrag[];
  readonly gesamtpreis: Money;
  readonly baugruppen: BaugruppenEintrag[][];
} {
  // Als erstes werden die Artikel / Objekte gesammelt, indem über das Arrangement iteriert wird
  // das Ergebnis wird in die verschiedenen Bags / Maps geschrieben
  // Die Gruppierung erfolgt pro Element / Tor mit Torpfosten / Dreifachtor
  const groupInfoMap = collectObjectsAndArtikel(arrangement);

  // Die Zuschnittsoptimierung mapped die nicht zugeschnittenen Objekte der
  // grouped objektBreiteBag auf die grouped objektBag um.
  // Sie zuschnittsoptimiert alle zugeschnittenen Objekte und packt sie in die
  // UNGROUPED ObjektBag.
  performZuschnittsoptimierung(groupInfoMap);

  // führt die Set_Artikelrelationen und Artikelrelationen für die grouped und UNGROUPED aus,
  // d.h. mapped die objektBag in die artikelBag. Die grouped werden dabei in die UNGROUPED
  // zusammengezählt.
  mapObjectsToArtikels(groupInfoMap);

  // aus der UNGROUPED artikelBag wird die Bestellliste
  const { artikelToElementIndexBagMap, elementIndexToGroupInfo } =
    mapArtikelToElementIndexBag(groupInfoMap);

  const { artikelToJacoIdBagMap } = mapArtikelToJacoIdBag(groupInfoMap);

  const bestellliste = mapArtikelBagToBestellliste(
    groupInfoMap.get(UNGROUPED).artikelBag,
    artikelToElementIndexBagMap,
    elementIndexToGroupInfo,
    artikelToJacoIdBagMap
  );

  if (arrangement.userDefinedArticles) {
    bestellliste.push(
      ...arrangement.userDefinedArticles.map(({ amount, price, description }) => {
        const einzel = toCent(price);
        const gesamt = amount * einzel;
        return {
          Anzahl: amount,
          Einzelpreis: unCent(einzel),
          Preis: unCent(gesamt),
          Mass: null,
          ElementIndizes: [],
          JacoIds: [],
          UserDefined: true,
          ArtikelID: null,
          VPE: 1,
          ArtikeltextPreisuebersicht: description,
          id: null,
        };
      })
    );
  }
  const gesamtpreis = bestelllistenSumme(bestellliste);

  // aus den grouped artikelBag wird die Baugruppen
  const baugruppen = mapGroupArtikelBagToBaugruppen(
    groupInfoMap,
    artikelToElementIndexBagMap,
    artikelToJacoIdBagMap
  );

  return { bestellliste, gesamtpreis, baugruppen } as const;
}

interface MiniBag<K> extends ForEach<K> {
  get(key: K): number;

  inc(key: K, value: number);
}

interface GetSumBag<K> {
  get(key: K): number;

  subTreeForEach(keys: any, callback: (value: number, key: any) => void);
}

const combinedGetBag = <K>(bagWrite: GetSumBag<K>, bagRead: GetSumBag<K>): GetSumBag<K> => {
  return {
    get(key: K): number {
      return bagWrite.get(key) + bagRead.get(key);
    },
    subTreeForEach(keys: any, callback: (value: number, key: any) => void) {
      bagWrite.subTreeForEach(keys, callback);
      bagRead.subTreeForEach(keys, callback);
    },
  };
};

const getZuschnittsoptimiert = (
  ObjektMeta: MKeyTable,
  Variabel: string
): MZuschnittsoptimiert | false => {
  const zuschnittsoptimiert = ObjektMeta["Zuschnittsoptimiert"]?.[0] as MZuschnittsoptimiert;
  return Variabel == "bauseits" && zuschnittsoptimiert?.Zuschnitt ? zuschnittsoptimiert : false;
};

function collectObjectsAndArtikel(root: TArtikelSource) {
  APP.logStückliste &&
    console.log("-- root.collectArtikel ----------------------------------------");

  const iteratorCalls = [];

  const groupInfoMap = new Map<TObjectGroupTag, GroupInfo>();
  const ensureGroup = ensureMap(groupInfoMap, (groupTag: TObjectGroupTag) => new GroupInfo());
  ensureGroup(UNGROUPED);

  const iterator: StücklistenIterator = ({
    ObjektMeta,
    Artikel,
    Anzahl,
    Breite,
    MassOrBauseitsTag,
    ElementIndex = undefined,
    JacoId = undefined,
    ObjectGroupTag = undefined,
    Variabel = undefined,
  }: Partial<StücklistenIteratorParams>) => {
    // ---
    Breite = Breite == undefined ? undefined : Maths.round(Breite, 2);
    Anzahl = Anzahl ?? 1;
    if (!Maths.isFinite(Anzahl)) {
      console.error(`Anzahl ist keine endliche Zahl: ${Anzahl}`);
      debugger;
    }
    ObjectGroupTag = ObjectGroupTag ?? UNGROUPED;
    // nur zum debuggen ---
    iteratorCalls.push({
      ObjektMeta: ObjektMeta?.Key,
      Artikelnummer: Artikel?.Artikelnummer,
      Anzahl,
      Breite,
      ObjectGroupTag,
      ElementIndex,
      MassOrBauseitsTag,
      Variabel,
    });
    const groupInfo = ensureGroup(ObjectGroupTag);
    groupInfo.ObjectGroupTag = ObjectGroupTag;
    if (ElementIndex != undefined) {
      groupInfo.ElementIndex = ElementIndex;
    }
    if (MassOrBauseitsTag) {
      groupInfo.MassOrBauseitsTag = MassOrBauseitsTag;
    }
    if (Variabel) {
      groupInfo.Variabel = Variabel;
    }
    if (Artikel) {
      groupInfo.artikelBag.inc(Artikel, Anzahl);
      groupInfo.ensureJacoSetOfArtikel(Artikel).add(JacoId);
    }
    if (ObjektMeta) {
      groupInfo.objektBreiteBagVorZuschnitt.inc([ObjektMeta, Breite], Anzahl);
      groupInfo.ensureJacoSetOfObjekt(ObjektMeta).add(JacoId);
      if (
        getZuschnittsoptimiert(ObjektMeta, groupInfo.MassOrBauseitsTag) &&
        !Maths.isFinite(Breite)
      ) {
        console.error(
          `das zuschnittsoptimierte Objekt ${ObjektMeta.Key} muss eine Breite haben: ${Breite}`
        );
        debugger;
      }
    }
  };

  root.collectArtikel(iterator);

  APP.logStückliste && console.table(iteratorCalls);
  //
  APP.logStückliste && console.log("collected objects:");
  APP.logStückliste &&
    console.table(
      Array.from(groupInfoMap).flatMap(([ObjectGroupTag, groupInfo]) =>
        Array.from(groupInfo.objektBreiteBagVorZuschnitt).map(([[ObjektMeta, Breite], Anzahl]) => ({
          ObjectGroupTag,
          ElementIndex: groupInfo.ElementIndex,
          ObjektMeta: ObjektMeta.Key,
          Breite,
          Anzahl,
          MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
          Variabel: groupInfo.Variabel,
          JacoIds: groupInfo.objektJacoIdsMap.get(ObjektMeta)
            ? Array.from(groupInfo.objektJacoIdsMap.get(ObjektMeta)).join(",")
            : "",
        }))
      )
    );
  APP.logStückliste && console.log("collected artikels:");
  APP.logStückliste &&
    console.table(
      Array.from(groupInfoMap).flatMap(([ObjectGroupTag, groupInfo]) =>
        Array.from(groupInfo.artikelBag).map(([Artikel, Anzahl]) => ({
          ObjectGroupTag,
          Artikel: Artikel.ArtikelID,
          Anzahl,
          MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
          Variabel: groupInfo.Variabel,
        }))
      )
    );

  return groupInfoMap;
}

function performZuschnittsoptimierung(groupInfoMap: Map<TObjectGroupTag | undefined, GroupInfo>) {
  const zuschnittBreiteBag = new MultiBag<[MKeyTable, MZuschnittsoptimiert, Size]>();
  for (let [ObjectGroupTag, groupInfo] of groupInfoMap) {
    for (const [[objektMeta, Breite], Anzahl] of groupInfo.objektBreiteBagVorZuschnitt) {
      const zuschnittsoptimiert = getZuschnittsoptimiert(objektMeta, groupInfo.Variabel);
      if (
        zuschnittsoptimiert &&
        !(Maths.isFinite(Breite) && Breite > Math.max(...zuschnittsoptimiert.Standardbreite))
      ) {
        zuschnittBreiteBag.inc([objektMeta, zuschnittsoptimiert, Breite], Anzahl);
        groupInfo.objektBreiteBagGruppenzuschnitt.inc([objektMeta, Breite], Anzahl);
      } else {
        if (zuschnittsoptimiert) {
          console.warn(
            `Zuschnittsoptimierung: Breite ${Breite} von ${(objektMeta as any).Key} übersteigt max. Standardbreite ${Math.max(...zuschnittsoptimiert.Standardbreite)}. Übersprungen.`
          );
        }
        groupInfo.objektBreiteBag.inc([objektMeta, Breite], Anzahl);
      }
    }
  }
  const ungroupedObjektZuschnittBag = groupInfoMap.get(UNGROUPED).objektBreiteBag;
  for (const [objektMeta, zuschnittBreiteMap] of zuschnittBreiteBag.rootMap) {
    for (const [zuschnittsoptimiert, breiteAnzahlMap] of zuschnittBreiteMap) {
      const bins = binPackingFirstFitDecreasing(
        zuschnittsoptimiert.Standardbreite,
        breiteAnzahlMap
      );
      const breitenBag = new Bag<Size>();
      bins.forEach((bin) => breitenBag.inc(bin.binSize, 1));
      if (
        [...breitenBag.keys()].every(
          (breite) => !zuschnittsoptimiert.Standardbreite.includes(breite)
        )
      ) {
        console.error(
          "keine Standardbreiten nach Zuschnitt",
          breitenBag.keys(),
          zuschnittsoptimiert.Standardbreite
        );
        debugger;
      }
      [...breitenBag].forEach(([Standardbreite, optimierteAnzahl]) => {
        ungroupedObjektZuschnittBag.inc([objektMeta, Standardbreite], optimierteAnzahl);
      });
    }
  }
}

function getUnechtenArtikel(
  unechteArtikelBag: MiniBag<MArtikel>,
  Artikel: MArtikel
): MArtikel | undefined {
  if (unechteArtikelBag == null) {
    return undefined;
  }
  const unechterArtikel = Artikel.potentiellUnechteArtikel?.find(
    (artikel) => unechteArtikelBag.get(artikel) > 0
  );
  if (!unechterArtikel) {
    return undefined;
  }
  return unechterArtikel;
}

function maybeErzeugeVirtuellenArtikel(
  ensureVirtuellenArtikel: TEnsureVirtuellenArtikel,
  unechteArtikelBag: MiniBag<MArtikel>,
  Artikel: MArtikel,
  variabel: TVariabel,
  MassOrBauseitsTag: TMassOrBauseitsTag
) {
  const unechterArtikel = getUnechtenArtikel(unechteArtikelBag, Artikel);
  const mass = Artikel.Massartikel && variabel === "fertigung" ? MassOrBauseitsTag : undefined;
  if (unechterArtikel || mass) {
    const virtuellerArtikel = ensureVirtuellenArtikel([Artikel, unechterArtikel, mass]);
    return virtuellerArtikel;
  }
  return Artikel;
}

interface MapArtikelrelationsParams {
  mObjektBreiteBag: MultiBag<[MKeyTable, Size]>;
  artikelBag: MiniBag<MArtikel>;
  ensureJacoSetOfArtikel: (k: MArtikel) => Set<number>;
  objektJacoIdsMap: Map<MKeyTable, Set<number>>;
  filterNurGruppe: boolean | null;
  filterUnechteArtikel: boolean;
  nurVirtuelleArtikel: boolean;
  Variabel: TVariabel | null;
  MassOrBauseitsTag: TMassOrBauseitsTag | null;
  unechteArtikelBag: MiniBag<MArtikel> | null;
  ensureVirtuellenArtikel: TEnsureVirtuellenArtikel;
  logPrefix: string;
}

function getBreite0Summe(
  mObjektBreiteBag: GetSumBag<[MKeyTable, Size]>,
  Objekt: MKeyTable,
  StandardBreiteIntervall: Vector2
): Amount {
  let sum = 0;
  mObjektBreiteBag.subTreeForEach([Objekt], (anzahl: Amount, restKeys: Size[]) => {
    const breite = restKeys[0];
    if (
      StandardBreiteIntervall == null ||
      breite == null ||
      (StandardBreiteIntervall.x < breite && breite <= StandardBreiteIntervall.y)
    ) {
      sum += anzahl;
    }
  });
  // sum > 0 &&
  //   APP.logStückliste &&
  //   console.log("getBreite0Summe", {
  //     StandardBreite: StandardBreiteIntervall,
  //     Key: Objekt?.Key,
  //     sum,
  //   });
  return sum;
}

export function mapArtikels({
  mObjektBreiteBag,
  artikelBag,
  ensureJacoSetOfArtikel,
  objektJacoIdsMap,
  filterNurGruppe,
  filterUnechteArtikel,
  nurVirtuelleArtikel,
  Variabel,
  MassOrBauseitsTag,
  unechteArtikelBag,
  ensureVirtuellenArtikel,
  logPrefix,
}: MapArtikelrelationsParams) {
  const orderSetErsetzen = (s: SetArtikel) => (s.SetErsetzen ? 1 : 0);
  const orderVariabel = (s: SetArtikel) => (s.Variabel ? 1 : 0);
  const orderBreiteMinMax = (s: SetArtikel) => (s.BreiteMinMax ? 1 : 0);
  const gesamtObjektAnzahl = (s: SetArtikel) =>
    s.set.reduce(
      (acc, set) =>
        acc + Math.min(set.Anzahl, getBreite0Summe(mObjektBreiteBag, set.Objekt, s.BreiteMinMax)),
      0
    );

  const setArtikels: SetArtikel[] = filterSetArtikel(
    filterNurGruppe,
    filterUnechteArtikel,
    Variabel
  );

  const setArtikelMitMinAnzahl = setArtikels
    .filter(({ id, set }) =>
      set.every(
        ({ Objekt, BreiteMinMax, MinAnzahl }) =>
          getBreite0Summe(mObjektBreiteBag, Objekt, BreiteMinMax) >= MinAnzahl
      )
    )
    .sort(
      (s1: SetArtikel, s2: SetArtikel) =>
        // Sortiere nach am besten passender (maximaler) Anzahl (setArtikelAnzahlVorhanden) zuerst:
        // es kann konkurrierende SetArtikel-Objekte geben, wie bei Zaunfeldsets mit gleichem Profil
        // aber unterschiedlicher Abschlussleiste. Die Abschlussleisten-Minanzahl ist in diesen Sets jeweils gleich 0.
        // Falls passende Abschlussleisten existieren, soll der am besten passende SetArtikel verwendet werden.
        // Ein anderes Sortierkriterium ist, wie spezifisch die Regeln sind: Eine Regel mit mehr Einschränkungen
        // wie Variabel und BreiteMinMax wird vor anderen Regeln ausgeführt.
        orderSetErsetzen(s1) - orderSetErsetzen(s2) ||
        orderVariabel(s2) - orderVariabel(s1) ||
        orderBreiteMinMax(s2) - orderBreiteMinMax(s1) ||
        gesamtObjektAnzahl(s2) - gesamtObjektAnzahl(s1) ||
        s1.Anzahl - s2.Anzahl
    );

  setArtikelMitMinAnzahl.forEach((setArtikel) => {
    let { set, Anzahl, Artikel, SetErsetzen, id } = setArtikel;
    Artikel = maybeErzeugeVirtuellenArtikel(
      ensureVirtuellenArtikel,
      unechteArtikelBag,
      Artikel,
      Variabel,
      MassOrBauseitsTag
    );

    if (nurVirtuelleArtikel && Artikel === setArtikel.Artikel) {
      return;
    }

    // mObjektBreiteBagBuffer dient dazu, damit der Fall SetErsetzen==false die mObjektBag nicht verändert
    const mObjektBreiteBagBuffer = new MultiBag<[MKeyTable, Size]>();
    // mObjektBreiteBagCombined gibt für jede Anfrage die Summe der Einzelbags zurück
    const mObjektBreiteBagCombined = combinedGetBag(mObjektBreiteBagBuffer, mObjektBreiteBag);

    while (
      set.every(
        ({ Objekt, BreiteMinMax, MinAnzahl, Anzahl }) =>
          getBreite0Summe(mObjektBreiteBagCombined, Objekt, BreiteMinMax) >= MinAnzahl
      )
    ) {
      APP.logStückliste &&
        console.log(logPrefix, "SetArtikelrelation", {
          id,
          ArtikelID: logArtikelMaybeVirtuell(Artikel),
          Anzahl,
          setArtikelMitMinAnzahl,
          setArtikels,
        });
      artikelBag.inc(Artikel, Anzahl);
      if (Artikel.unechterArtikel) {
        unechteArtikelBag.inc(Artikel.unechterArtikel, -Anzahl);
      }
      set.forEach(({ Objekt, BreiteMinMax, MinAnzahl, Anzahl }) => {
        const möglicheAnzahl = Math.min(
          Anzahl,
          getBreite0Summe(mObjektBreiteBagCombined, Objekt, BreiteMinMax)
        );
        // Achtung! Breite ist die BreiteMinMax.y (also die Max-Breite) des Sets, nicht die Breite des Objektes
        // Das muss so sein, weil die Set-Breite 0 bedeutet, dass die Anzahl über alle Breiten summiert
        // wird, und in dem Fall unklar ist, von Objekten welcher Breite man die möglicheAnzahl abziehen sollte -
        // also wird einfach Breite==0 genommen
        // Ergebnis sind z.B. negative Anzahlen bei Breite 0 und zusätzlich positive Anzahlen bei
        // anderen Breiten, die sich bei Summierung aber gegenseitig aufheben.
        const Breite = BreiteMinMax?.y || 0;
        const bag = SetErsetzen ? mObjektBreiteBag : mObjektBreiteBagBuffer;
        bag.inc([Objekt, Breite], -möglicheAnzahl);
        ensureJacoSetOfArtikel(Artikel).addAll(objektJacoIdsMap.get(Objekt));
        APP.logStückliste &&
          console.log("        ", {
            Objekt: Objekt.Key,
            Breite,
            objektDelta: -möglicheAnzahl,
          });
      });
    }
  });
}

function logArtikelMaybeVirtuell(Artikel: MArtikel) {
  if (Artikel.unechterArtikel) {
    return (
      "virtuell " +
      Artikel.ArtikelID +
      " (+ unechter Artikel " +
      Artikel.unechterArtikel.ArtikelID +
      ")"
    );
  }
  return Artikel.ArtikelID;
}

function isSeparateGroupInBestellliste(groupInfo: GroupInfo) {
  return groupInfo.Variabel === "fertigung";
}

function mapObjectsToArtikels(groupInfoMap: Map<TObjectGroupTag | undefined, GroupInfo>) {
  APP.logStückliste && console.log("Set_Artikelrelation ausführen:");

  const ensureVirtuellenArtikel = createEnsureVirtuellenArtikel();

  // In ungrouped werden in dieser Funktion alle Infos für die Bestellliste gesammelt. Es werden alle
  // Artikel und Objekte aufsummiert über alle Gruppen plus die, welche sowieso keiner Gruppe
  // angehörten
  const ungrouped = groupInfoMap.get(UNGROUPED);

  // Alle gruppierten Infos wandern in dieser Funktion in die Baugruppen (Gruppe pro Elemnt/Pfosten)
  const groups = Array.from(groupInfoMap)
    .filter(([ObjectGroupTag]) => ObjectGroupTag !== UNGROUPED)
    .map(([_, groupInfo]) => groupInfo);

  // die Anzahlen der objektBreiteBag sind für die Bestellliste relevant
  APP.logStückliste && console.log("nach Zuschnitt objektBreiteBag:");
  APP.logStückliste &&
    console.table(
      [ungrouped].concat(groups).flatMap((groupInfo) =>
        groupInfo.objektBreiteBag.map((Anzahl, [Objekt, Breite]) => ({
          ObjektMeta: Objekt.Key,
          Breite,
          Anzahl,
          ObjectGroupTag: groupInfo.ObjectGroupTag,
          ElementIndex: groupInfo.ElementIndex,
          JacoIds: groupInfo.objektJacoIdsMap.get(Objekt)?.join(","),
        }))
      )
    );

  // die objektBreiteBagGruppenzuschnitt darf nur für die Baugruppen verwendet werden, weil
  // in der für die Bestellliste relevante UNGROUPED.objektBreiteBag dieselben Anzahlen stehen
  // und ansonsten etwas doppelt gezählt würde
  APP.logStückliste && console.log("nach Zuschnitt objektBreiteBagGruppenzuschnitt:");
  APP.logStückliste &&
    console.table(
      [ungrouped].concat(groups).flatMap((groupInfo) =>
        groupInfo.objektBreiteBagGruppenzuschnitt.map((Anzahl, [Objekt, Breite]) => ({
          ObjektMeta: Objekt.Key,
          Breite,
          Anzahl,
          ObjectGroupTag: groupInfo.ObjectGroupTag,
          ElementIndex: groupInfo.ElementIndex,
          JacoIds: groupInfo.objektJacoIdsMap.get(Objekt)?.join(","),
        }))
      )
    );

  // 1. Artikelklammern vorverarbeiten: unechte Artikel werden vorverarbeitet und separat gesammelt in der unechteArtikelBag
  // unechte Artikel müssen in einer Klammer stehen, müssen daher Teil eines Elementes sein
  // Aus Artikeln, welche mit unechten Artikeln in einer Klammer stehen, werden virtuelle Artikel,
  // d.h. eine Kombination aus echtem und unechtem Artikel einer Klammer
  groups.forEach((groupInfo) => {
    mapArtikels({
      mObjektBreiteBag: groupInfo.objektBreiteBag,
      artikelBag: groupInfo.unechteArtikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: null,
      filterUnechteArtikel: true,
      nurVirtuelleArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      unechteArtikelBag: null,
      ensureVirtuellenArtikel,
      logPrefix: "1. unechte Artikel vorverarbeiten objektBreiteBag " + groupInfo.ObjectGroupTag,
    });
    mapArtikels({
      mObjektBreiteBag: groupInfo.objektBreiteBagGruppenzuschnitt,
      artikelBag: groupInfo.unechteArtikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: null,
      filterUnechteArtikel: true,
      nurVirtuelleArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      unechteArtikelBag: null,
      ensureVirtuellenArtikel,
      logPrefix:
        "1. unechte Artikel vorverarbeiten objektBreiteBagGruppenzuschnitt " +
        groupInfo.ObjectGroupTag,
    });
  });

  // 1.1 Artikelklammern vorverarbeiten auch für die ungrouped.
  // Jetzt kommt ein überraschender Trick: Alle ungruppierten Artikel werden hierbei als Bauseits angenommen.
  // Warum? Na wenn es eine Regel ist, die danach fragt, ob ein gegebenes
  // Objekt bauseits ist, dann muss das Objekt der Regel eigentlich immer einem Element entstammen. Dann kann
  // es aber nur in der ungruppierten Gruppe sein, wenn es zugeschnitten ist, woraus folgt, dass
  // es bauseits war.
  mapArtikels({
    mObjektBreiteBag: ungrouped.objektBreiteBag,
    artikelBag: ungrouped.unechteArtikelBag,
    ensureJacoSetOfArtikel: ungrouped.ensureJacoSetOfArtikel,
    objektJacoIdsMap: ungrouped.objektJacoIdsMap,
    filterNurGruppe: null,
    filterUnechteArtikel: true,
    nurVirtuelleArtikel: false,
    Variabel: "bauseits",
    MassOrBauseitsTag: "bauseits",
    unechteArtikelBag: null,
    ensureVirtuellenArtikel,
    logPrefix: "1.1 unechte Artikel vorverarbeiten objektBreiteBag UNGROUPED",
  });

  APP.logStückliste && console.log("unechte Artikel aufsummiert:");
  APP.logStückliste &&
    console.table(
      [ungrouped].concat(groups).flatMap((groupInfo) =>
        groupInfo.unechteArtikelBag.map((Anzahl, Artikel) => ({
          Artikel: logArtikelMaybeVirtuell(Artikel),
          Anzahl,
          ObjectGroupTag: groupInfo.ObjectGroupTag,
          ElementIndex: groupInfo.ElementIndex,
          JacoIds: groupInfo.artikelJacoIdsMap.get(Artikel)?.join(","),
        }))
      )
    );

  // 2. Hier müssen alle Artikelrelationen ausgeführt werden, die virtuelle Artikel erzeugen
  // Grund ist: die virtuellen Artikel können nur innerhalb der Gruppe ermittelt werden
  // Andererseits muss man hier so wenige Relationen wie möglich ausführen, weil ansonsten
  // die Artikelsets unter 3. nicht mehr greifen können wie z.B. gruppenübergreifende Zaunfeld-Sets
  groups.forEach((groupInfo) => {
    mapArtikels({
      mObjektBreiteBag: groupInfo.objektBreiteBag,
      artikelBag: groupInfo.artikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: null,
      filterUnechteArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      nurVirtuelleArtikel: true,
      unechteArtikelBag: groupInfo.unechteArtikelBag,
      ensureVirtuellenArtikel,
      logPrefix: "2. Artikelmapping nur Gruppe objektBreiteBag " + groupInfo.ObjectGroupTag,
    });
  });

  // 2.1 Das gleiche auch für die ungrouped unter der Annahme, dass es bauseits Artikel sind,
  // analog zu 1.1
  mapArtikels({
    mObjektBreiteBag: ungrouped.objektBreiteBag,
    artikelBag: ungrouped.artikelBag,
    ensureJacoSetOfArtikel: ungrouped.ensureJacoSetOfArtikel,
    objektJacoIdsMap: ungrouped.objektJacoIdsMap,
    filterNurGruppe: null,
    filterUnechteArtikel: false,
    Variabel: "bauseits",
    MassOrBauseitsTag: "bauseits",
    nurVirtuelleArtikel: true,
    unechteArtikelBag: ungrouped.unechteArtikelBag,
    ensureVirtuellenArtikel,
    logPrefix: "2.1 Artikelmapping objektBreiteBag UNGROUPED",
  });

  // 3. Jetzt müssen die Regeln mit NurGruppe ausgeführt werden (z.B. 3fach-Torpfostensets),
  // also alle Regeln, die nur innerhalb von Gruppen ausgeführt werden sollen und nicht
  // Zaun-Übergreifend gelten (d.h. zwingend vor Schritt 4, dem Aufsummieren nach UNGROUPED).
  groups.forEach((groupInfo) => {
    mapArtikels({
      mObjektBreiteBag: groupInfo.objektBreiteBag,
      artikelBag: groupInfo.artikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: true,
      filterUnechteArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      nurVirtuelleArtikel: false,
      unechteArtikelBag: null,
      ensureVirtuellenArtikel,
      logPrefix: "3. Artikelmapping NurGruppe objektBreiteBag " + groupInfo.ObjectGroupTag,
    });
  });

  // Aufsummieren aller Objekte und Artikel aus den Gruppen für die Bestellliste
  // In der Bestellliste können Artikel gruppenübergreifend zusammengefasst werden,
  // weiterhin zugeschnitten sein
  groups.forEach((groupInfo) => {
    ungrouped.objektBreiteBag.incAll(groupInfo.objektBreiteBag);
    ungrouped.artikelBag.incAll(groupInfo.artikelBag);
    // JacoIds der Gruppenobjekte in UNGROUPED mergen, damit Schritt 4 (Bestellliste)
    // auch für aus Gruppen stammende Objekte die JacoIds kennt
    groupInfo.objektJacoIdsMap.forEach((jacoIds, objekt) => {
      ungrouped.ensureJacoSetOfObjekt(objekt).addAll(jacoIds);
    });
  });

  APP.logStückliste && console.log("ungroupedObjektBag nach Aufsummierung:");
  APP.logStückliste &&
    console.table(
      ungrouped.objektBreiteBag.map((Anzahl, [ObjektMeta, Breite]) => ({
        ObjektMeta: ObjektMeta.Key,
        Breite,
        Anzahl,
      }))
    );

  // 4. Alle Regeln für die Bestellliste anwenden
  mapArtikels({
    mObjektBreiteBag: ungrouped.objektBreiteBag,
    artikelBag: ungrouped.artikelBag,
    ensureJacoSetOfArtikel: ungrouped.ensureJacoSetOfArtikel,
    objektJacoIdsMap: ungrouped.objektJacoIdsMap,
    filterNurGruppe: null,
    filterUnechteArtikel: false,
    Variabel: null,
    MassOrBauseitsTag: null,
    unechteArtikelBag: null,
    nurVirtuelleArtikel: false,
    ensureVirtuellenArtikel,
    logPrefix: "4. Artikelmapping objektBreiteBag UNGROUPED",
  });

  // 5. Alle fehlenden Regeln für die Baugruppen ausführen
  //    erst jetzt, weil sonst die Bestellliste verfällscht würde
  groups.forEach((groupInfo) => {
    const mObjektBreiteBag = new MultiBag<[MKeyTable, Size]>();
    mObjektBreiteBag.incAll(groupInfo.objektBreiteBag);
    mObjektBreiteBag.incAll(groupInfo.objektBreiteBagGruppenzuschnitt);
    // analog zu 2. nachholen für alle übrig gebliebenen Objekte
    mapArtikels({
      mObjektBreiteBag,
      artikelBag: groupInfo.artikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: null,
      filterUnechteArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      nurVirtuelleArtikel: true,
      unechteArtikelBag: groupInfo.unechteArtikelBag,
      ensureVirtuellenArtikel,
      logPrefix:
        "5.2 Artikelmapping objektBreiteBag+objektBreiteBagGruppenzuschnitt " +
        groupInfo.ObjectGroupTag,
    });
    // analog zu 4. nachholen für alle übrig gebliebenen Objekte
    mapArtikels({
      mObjektBreiteBag,
      artikelBag: groupInfo.artikelBag,
      ensureJacoSetOfArtikel: groupInfo.ensureJacoSetOfArtikel,
      objektJacoIdsMap: groupInfo.objektJacoIdsMap,
      filterNurGruppe: null,
      filterUnechteArtikel: false,
      Variabel: groupInfo.Variabel,
      MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
      unechteArtikelBag: groupInfo.unechteArtikelBag,
      nurVirtuelleArtikel: false,
      ensureVirtuellenArtikel,
      logPrefix:
        "5.4 Artikelmapping objektBreiteBag+objektBreiteBagGruppenzuschnitt " +
        groupInfo.ObjectGroupTag,
    });
  });

  APP.logStückliste && console.log("artikels:");
  APP.logStückliste &&
    console.table(
      Array.from(groupInfoMap).flatMap(([ObjectGroupTag, groupInfo]) =>
        Array.from(groupInfo.artikelBag).map(([artikel, Anzahl]) => ({
          Anzahl,
          PreisInclMwst: artikel.PreisInclMwst,
          ArtikelID: logArtikelMaybeVirtuell(artikel),
          ArtikeltextPreisuebersicht: artikel.ArtikeltextPreisuebersicht,
          Stuecklistensortierung: artikel.Stuecklistensortierung,
          MassOrBauseitsTag: groupInfo.MassOrBauseitsTag,
          Variabel: groupInfo.Variabel,
          ObjectGroupTag,
          ElementIndex: groupInfo.ElementIndex,
          JacoIds: groupInfo.artikelJacoIdsMap.get(artikel)?.join(","),
        }))
      )
    );
}

function mapArtikelToElementIndexBag(groupInfoMap: Map<TObjectGroupTag, GroupInfo>): {
  artikelToElementIndexBagMap: Map<MArtikel, Bag<Index>>;
  elementIndexToGroupInfo: Map<Index, GroupInfo>;
} {
  const artikelToElementIndexBagMap = new Map<MArtikel, Bag<Index>>();
  const ensureBag = ensureMap(artikelToElementIndexBagMap, () => new Bag<Index>());
  const elementIndexToGroupInfo = new Map<Index, GroupInfo>();
  groupInfoMap.forEach((groupInfo) =>
    groupInfo.artikelBag.forEach((Anzahl, Artikel) => {
      ensureBag(Artikel).inc(groupInfo.ElementIndex, Anzahl);
      elementIndexToGroupInfo.set(groupInfo.ElementIndex, groupInfo);
    })
  );
  return { artikelToElementIndexBagMap, elementIndexToGroupInfo };
}

function mapArtikelToJacoIdBag(groupInfoMap: Map<TObjectGroupTag, GroupInfo>): {
  artikelToJacoIdBagMap: Map<MArtikel, Bag<number>>;
  jacoIdToGroupInfo: Map<number, GroupInfo>;
} {
  const artikelToJacoIdBagMap = new Map<MArtikel, Bag<number>>();
  const ensureBag = ensureMap(artikelToJacoIdBagMap, () => new Bag<number>());
  const jacoIdToGroupInfo = new Map<number, GroupInfo>();
  groupInfoMap.forEach((groupInfo) =>
    groupInfo.artikelBag.forEach((Anzahl, Artikel) => {
      const jacoIds = groupInfo.artikelJacoIdsMap.get(Artikel);
      if (jacoIds) {
        jacoIds.forEach((jacoId) => {
          ensureBag(Artikel).inc(jacoId, Anzahl);
          jacoIdToGroupInfo.set(jacoId, groupInfo);
        });
      }
    })
  );
  return { artikelToJacoIdBagMap, jacoIdToGroupInfo };
}

const mapUnechteArtikel = ([artikel, anzahl]: ArtikelAmount): ArtikelAmount[] =>
  artikel.unechterArtikel
    ? [
        [artikel, anzahl],
        [artikel.unechterArtikel, anzahl],
      ]
    : [[artikel, anzahl]];

export function filterArtikelsGesperrtOrZero(artikelAmounts: ArtikelAmount[]): ArtikelAmount[] {
  return artikelAmounts.filter(
    ([artikel, anzahl]) => !artikel.Gesperrt && Math.abs(anzahl) > 0.00001
  );
}

function getElementIndizes(
  artikelToElementIndexBagMap: Map<MArtikel, Bag<Index>>,
  artikel: MArtikel
): Index[] {
  return artikelToElementIndexBagMap.has(artikel)
    ? Array.from(artikelToElementIndexBagMap.get(artikel).keys()).filter((i: Index) => i != null)
    : [];
}

function getJacoIds(
  artikelToJacoIdBagMap: Map<MArtikel, Bag<number>>,
  artikel: MArtikel
): number[] {
  return artikelToJacoIdBagMap.has(artikel)
    ? Array.from(artikelToJacoIdBagMap.get(artikel).keys()).filter((i: number) => i != null)
    : [];
}

function specialGroupSortingForFertigungsArtikelForBestellliste(
  artikelAmounts: ArtikelAmount[],
  artikelToElementIndexBagMap: Map<MArtikel, Bag<Index>>,
  elementIndexToGroupInfo: Map<Index, GroupInfo>
) {
  // trenne die ArtikelAmounts in die normalen Artikel und in die Fertigungselemente als separate Gruppen
  const separateGroupes = new Map<Index, ArtikelAmount[]>();
  const ensureSeparateGroupForIndex = ensureMap(
    separateGroupes,
    (index: Index) => [] as ArtikelAmount[]
  );
  const artikelAmountsWithoutGroups = artikelAmounts
    .map(([artikel, amountOfArtikel]): ArtikelAmount => {
      const elementIndexBagMap = artikelToElementIndexBagMap.get(artikel);
      elementIndexBagMap.forEach((amountOfArtikelInElement: Amount, elementIndex: Index) => {
        const groupInfo = elementIndexToGroupInfo.get(elementIndex);
        if (!artikel.Unabhaengig && isSeparateGroupInBestellliste(groupInfo)) {
          amountOfArtikel -= amountOfArtikelInElement;
          const separateGroup = ensureSeparateGroupForIndex(elementIndex);
          separateGroup.push([artikel, amountOfArtikelInElement]);
        }
      });
      if (amountOfArtikel == 0) {
        return null;
      } else {
        return [artikel, amountOfArtikel];
      }
    })
    .filter((a) => a != null);

  // Fasse die separaten Gruppen (je eine Gruppe pro Fertigungselement) anhand des ArtikelKeys der
  // Gruppe als Bags zusammen (Summe aller ArtikelIDs)
  type ArtikelsKey = string;
  const getArtikelsKey = (artikelAmounts: ArtikelAmount[]): ArtikelsKey =>
    artikelAmounts
      .map(([artikel, amount]) => artikel.ArtikelID)
      .sort()
      .join("-");
  const artikelKeysToGroupArtikels = new Map<ArtikelsKey, Bag<MArtikel>>();
  const ensureSeparateGroupForArtikelKeys = ensureMap(
    artikelKeysToGroupArtikels,
    (artikelsKey: ArtikelsKey) => new Bag<MArtikel>()
  );
  [...separateGroupes.values()].forEach((artikelAmounts) => {
    const artikelsKey = getArtikelsKey(artikelAmounts);
    const bag = ensureSeparateGroupForArtikelKeys(artikelsKey);
    artikelAmounts.forEach(([artikel, amount]) => bag.inc(artikel, amount));
  });

  // Ermittle pro SeperaterGruppe die Sortierung der gesamten Gruppe innerhalb der Stückliste.
  // Innerhalb der Gruppe wird ebenfalls nach Stücklistensortierung aufsteigend sortiert - im Gegensatz
  // zur Baugruppe, wo nach Preis absteigend sortiert wird
  type SeparateGroupPseudoArtikel = {
    Stuecklistensortierung: number;
    artikelAmounts: ArtikelAmount[];
  };

  const sortiereNachStuecklistensortierungAufsteigend = (
    artikelAmounts: [MArtikel | SeparateGroupPseudoArtikel, Amount][]
  ) => artikelAmounts.sort((a, b) => a[0].Stuecklistensortierung - b[0].Stuecklistensortierung);

  const separateGroupArtikelAmountsSorted: [SeparateGroupPseudoArtikel, number][] = [
    ...artikelKeysToGroupArtikels.values(),
  ].map((bag) => {
    const artikelAmounts: ArtikelAmount[] = bag.map((amount: Amount, artikel: MArtikel) => [
      artikel,
      amount,
    ]);
    const Stuecklistensortierung = topSortierung(
      artikelAmounts.map(([artikel, amount]) => artikel)
    );
    sortiereNachStuecklistensortierungAufsteigend(artikelAmounts);
    const pseudoArtikel: SeparateGroupPseudoArtikel = { Stuecklistensortierung, artikelAmounts };
    return [pseudoArtikel, 1];
  });

  // Vereinige und sortiere die Gesamtheit der ArtikelAmounts und SeparateGroups
  const artikelAmountsAndGroups: [MArtikel | SeparateGroupPseudoArtikel, Amount][] = []
    .concat(artikelAmountsWithoutGroups)
    .concat(separateGroupArtikelAmountsSorted);
  sortiereNachStuecklistensortierungAufsteigend(artikelAmountsAndGroups);

  // Und klopfe schließlich die SeparateGroups wieder flach => am Ende hat man wieder die ArtikelAmounts, nur
  // eben mit den Fertigungsartikeln untereinander sortiert. Dadurch, dass Artikel den Fertigungs-Artikeln zugeordnet wurden,
  // können sie in dem Fall auch mehrmals in der Bestellliste erscheinen, also jeweils mit den Einzelanzahlen pro Element, d.h.
  // die Gesamtanzahl der Artikel bleibt unverändert
  const resultArtikelAmounts: ArtikelAmount[] = artikelAmountsAndGroups.flatMap(
    ([artikel, amount]) =>
      (artikel as SeparateGroupPseudoArtikel).artikelAmounts ?? [[artikel as MArtikel, amount]]
  );
  return resultArtikelAmounts;
}

function mapArtikelBagToBestellliste(
  artikelBag: Bag<MArtikel>,
  artikelToElementIndexBagMap: Map<MArtikel, Bag<Index>>,
  elementIndexToGroupInfo: Map<Index, GroupInfo>,
  artikelToJacoIdBagMap: Map<MArtikel, Bag<number>>
) {
  const artikelAmounts = specialGroupSortingForFertigungsArtikelForBestellliste(
    artikelBag.toArray(),
    artikelToElementIndexBagMap,
    elementIndexToGroupInfo
  );

  const bestellliste = filterArtikelsGesperrtOrZero(artikelAmounts)
    .flatMap(mapUnechteArtikel)
    .map(([artikel, anzahl]) => {
      let anzahlVPE = vpeTimes(artikel.VPE, anzahl);
      const customAnzahl = APP.arrangement.customAnzahlen[artikel.ArtikelID];
      const isAnzahlEditiert = !isNaN(customAnzahl) && customAnzahl != anzahlVPE;
      if (isAnzahlEditiert) anzahlVPE = customAnzahl;

      const Einzelpreis = toCent(artikel.PreisInclMwst);
      const Preis = anzahlVPE * Einzelpreis;
      const position: BestelllistenEintrag = {
        ...artikel,
        Anzahl: anzahlVPE,
        AnzahlEditiert: isAnzahlEditiert,
        Einzelpreis: unCent(Einzelpreis),
        Preis: unCent(Preis),
        Mass: artikel.mass,
        ElementIndizes: getElementIndizes(artikelToElementIndexBagMap, artikel),
        JacoIds: getJacoIds(artikelToJacoIdBagMap, artikel),
      };
      return position;
    });

  APP.logStückliste && console.log("bestellliste:");
  APP.logStückliste &&
    console.table(
      bestellliste.map((entry) => ({
        Anzahl: entry.Anzahl,
        PreisInclMwst: entry.PreisInclMwst,
        Einzelpreis: entry.Einzelpreis,
        Artikelnummer: entry.Artikelnummer,
        ArtikeltextPreisuebersicht: entry.ArtikeltextPreisuebersicht,
        unechterArtikel: entry.unechterArtikel?.ArtikelID,
        Mass: entry.Mass,
        Stuecklistensortierung: entry.Stuecklistensortierung,
        ElementIndizes: entry.ElementIndizes.join(","),
        JacoIds: entry.JacoIds.join(","),
      }))
    );

  return bestellliste;
}

function bestelllistenSumme(entries: BestelllistenEintrag[]) {
  return unCent(entries.reduce((summe, { Preis }) => summe + toCent(Preis), 0));
}

function groupArtikels(groupInfoMap: Map<TObjectGroupTag | undefined, GroupInfo>) {
  const artikelGroupTagArtikelBag = new MultiBag<[string, MArtikel]>();
  const getArtikelGroupTag = (artikels: MArtikel[], tag: string) => {
    const artikelIds = artikels
      .map((artikel) =>
        [artikel.ArtikelID, artikel.unechterArtikel?.ArtikelID ?? "", artikel.mass ?? ""].join("_")
      )
      .join(",");
    return artikelIds + ":" + tag;
  };
  groupInfoMap.forEach((groupInfo, ObjectGroupTag) => {
    if (ObjectGroupTag == UNGROUPED) {
      return;
    }
    const bagEntries = filterArtikelsGesperrtOrZero(groupInfo.artikelBag.toArray());

    const tag = groupInfo.MassOrBauseitsTag ?? "";
    const allArtikelGroupTag = getArtikelGroupTag(
      bagEntries.map(([artikel]) => artikel),
      tag
    );
    bagEntries.forEach(([artikel, anzahl]) => {
      artikelGroupTagArtikelBag.inc([allArtikelGroupTag, artikel], anzahl);
    });
  });

  APP.logStückliste && console.log("artikelGroupTagArtikelBag:");
  APP.logStückliste &&
    console.table(
      artikelGroupTagArtikelBag.map((Anzahl, [ArtikelGroupTag, artikel]) => ({
        Anzahl,
        ArtikelGroupTag,
        PreisInclMwst: artikel.PreisInclMwst,
        Artikelnummer: artikel.Artikelnummer,
        ArtikeltextPreisuebersicht: artikel.ArtikeltextPreisuebersicht,
        Stuecklistensortierung: artikel.Stuecklistensortierung,
      }))
    );
  return artikelGroupTagArtikelBag;
}

function topSortierung(artikels: MArtikel[]) {
  return Math.min(...artikels.map((a) => a.Stuecklistensortierung));
}

function mapGroupArtikelBagToBaugruppen(
  groupInfoMap: Map<TObjectGroupTag | undefined, GroupInfo>,
  artikelToElementIndexBagMap: Map<MArtikel, Bag<Index>>,
  artikelToJacoIdBagMap: Map<MArtikel, Bag<number>>
) {
  // alle Artikel neu nach Artikel-Gruppen und BauseitsTag zusammenfassen
  const artikelGroupTagArtikelBag = groupArtikels(groupInfoMap);

  // Artikel auf die Baugruppen mappen

  const mapArtikelGroupTagArtikelBagToBaugruppen = (): BaugruppenEintrag[][] =>
    Array.from(artikelGroupTagArtikelBag.rootMap)
      .filter(([ArtikelGroupTag]) => !Commons.isEmpty(ArtikelGroupTag))
      .map(([ArtikelGroupTag, artikelMap]) => {
        const MassOrBauseitsTag = ArtikelGroupTag.split(":")?.[1];
        const gruppenSortierung = topSortierung(Array.from(artikelMap.keys()));
        return Array.from(artikelMap)
          .sort((a, b) => b[0].PreisInclMwst - a[0].PreisInclMwst)
          .flatMap(mapUnechteArtikel)
          .map(([artikel, anzahl]) => {
            const anzahlVPE = vpeTimes(artikel.VPE, anzahl);
            const position: BaugruppenEintrag = {
              ...artikel,
              MassOrBauseitsTag,
              Anzahl: anzahlVPE,
              gruppenSortierung,
              ElementIndizes: getElementIndizes(artikelToElementIndexBagMap, artikel),
              JacoIds: getJacoIds(artikelToJacoIdBagMap, artikel),
            };
            return position;
          });
      })
      .sort((a, b) => a[0].gruppenSortierung - b[0].gruppenSortierung);

  const baugruppen = mapArtikelGroupTagArtikelBagToBaugruppen();

  APP.logStückliste && console.log("baugruppen:");
  APP.logStückliste &&
    baugruppen.map((liste, index) =>
      console.table(
        liste.map((entry) => ({
          index,
          Anzahl: entry.Anzahl,
          MassOrBauseitsTag: entry.MassOrBauseitsTag,
          PreisInclMwst: entry.PreisInclMwst,
          Artikelnummer: entry.Artikelnummer,
          ArtikeltextPreisuebersicht: entry.ArtikeltextPreisuebersicht,
          Stuecklistensortierung: entry.Stuecklistensortierung,
          gruppenSortierung: entry.gruppenSortierung,
          JacoIds: entry.JacoIds?.join(","),
        }))
      )
    );
  return baugruppen;
}

export interface SetArtikelObjekt {
  Objekt: MKeyTable;
  BreiteMinMax: Vector2;
  MinAnzahl: Amount;
  Anzahl: Amount;
}

export interface SetArtikel extends MArtikelrelation {
  set: SetArtikelObjekt[];
}

const filterSetArtikel: (
  nurGruppe: boolean | null,
  unechteArtikel: boolean,
  variabel: string
) => SetArtikel[] = memoizeUniversal((nurGruppe, unechteArtikel, variabel) => {
  const artikelrelation = [];

  APP.metadata.Artikelrelation.filter(
    (artikelSet: MArtikelrelation) =>
      (nurGruppe == null || artikelSet.NurGruppe == nurGruppe) &&
      istUnecht(artikelSet.Artikel) == unechteArtikel &&
      ((artikelSet.Variabel ?? []).length == 0 || artikelSet.Variabel.some((v) => v == variabel))
  ).forEach((artikelSet: MArtikelrelation) => {
    const sets = generateSetKombinationen(artikelSet);
    sets.forEach((set) => artikelrelation.push({ ...artikelSet, set }));
  });

  return artikelrelation.filter(({ set }) => set.length > 0);
});

const generateSetKombinationen = (artikelSet: MArtikelrelation) => {
  const keys = [1, 2, 3, 4];

  const aktiveObjekte = keys
    .map((i) => {
      const objekte = artikelSet[`Objekt${i}`];
      const anzahl = artikelSet[`Anzahl${i}`];
      return Array.isArray(objekte) && objekte.length > 0 && anzahl > 0
        ? {
            key: `Objekt${i}`,
            werte: objekte,
            Anzahl: artikelSet[`Anzahl${i}`],
            MinAnzahl: artikelSet[`MinAnzahl${i}`],
          }
        : null;
    })
    .filter(Boolean);

  const kombinationen = aktiveObjekte.reduce(
    (acc, feld) => {
      return acc.flatMap((kombi) =>
        feld.werte.map((wert) => ({
          ...kombi,
          [feld.key]: {
            Objekt: wert,
            BreiteMinMax: artikelSet.BreiteMinMax,
            Anzahl: feld.Anzahl,
            MinAnzahl: feld.MinAnzahl,
          },
        }))
      );
    },
    [{}]
  );

  return kombinationen.map((kombi) => {
    return Object.values(kombi);
  });
};
